import type { Settings } from '../save/Save';

/**
 * The settings and accessibility screen (SPEC §3.14).
 *
 * Every control writes straight into the live `Settings` object and calls back, so a change is
 * audible or visible before the player leaves the screen. That is not a convenience — the
 * accessibility options are the ones most likely to need trying rather than reading, and a motion
 * sensitivity toggle you cannot feel until you fly is a toggle nobody will trust.
 *
 * SPEC is pointed about this: "Motion sensitivity is a real concern in a cockpit-view game with
 * rolling shutter and camera shake — the reduction options must be genuinely effective, not
 * token." So the shake, aberration, grain and rolling-shutter switches each disable a real
 * pipeline stage rather than merely halving a constant.
 */

type Change = () => void;

export function buildSettingsPanel(settings: Settings, onChange: Change): HTMLDivElement {
  const root = document.createElement('div');

  root.appendChild(
    group('DISPLAY', [
      slider('Resolution scale', 0.5, 1, 0.05, settings.resolutionScale, (v) => {
        settings.resolutionScale = v;
        onChange();
      }),
      slider('Field of view', 60, 100, 1, settings.fieldOfViewDeg, (v) => {
        settings.fieldOfViewDeg = v;
        onChange();
      }),
      slider('Draw distance', 0.5, 1.5, 0.05, settings.drawDistanceScale, (v) => {
        settings.drawDistanceScale = v;
        onChange();
      }),
      slider('Particle density', 0, 1, 0.05, settings.particleDensity, (v) => {
        settings.particleDensity = v;
        onChange();
      }),
    ]),
  );

  const effects = Object.keys(settings.postEffects).map((key) =>
    toggle(`Post: ${key}`, settings.postEffects[key], (v) => {
      settings.postEffects[key] = v;
      onChange();
    }),
  );
  root.appendChild(group('POST-PROCESSING', effects));

  root.appendChild(
    group('AUDIO', [
      slider('Master', 0, 1, 0.05, settings.masterVolume, (v) => {
        settings.masterVolume = v;
        onChange();
      }),
      slider('Effects', 0, 1, 0.05, settings.effectsVolume, (v) => {
        settings.effectsVolume = v;
        onChange();
      }),
      // Kept last and separate on purpose: the RWR bus exists so this can stay up while the
      // effects bus goes down (SPEC §3.11).
      slider('RWR (threat tones)', 0, 1, 0.05, settings.rwrVolume, (v) => {
        settings.rwrVolume = v;
        onChange();
      }),
    ]),
  );

  root.appendChild(
    group('GAMEPLAY', [
      choice(
        'Difficulty',
        ['training', 'standard', 'realistic'],
        settings.difficulty,
        (v) => {
          settings.difficulty = v as Settings['difficulty'];
          onChange();
        },
      ),
      choice('Units', ['metric', 'imperial'], settings.units, (v) => {
        settings.units = v as Settings['units'];
        onChange();
      }),
    ]),
  );

  root.appendChild(
    group('ACCESSIBILITY', [
      toggle('Reduce camera shake', settings.reduceCameraShake, (v) => {
        settings.reduceCameraShake = v;
        onChange();
      }),
      toggle('Reduce rolling shutter', settings.reduceRollingShutter, (v) => {
        settings.reduceRollingShutter = v;
        onChange();
      }),
      toggle('Disable chromatic aberration', settings.disableChromaticAberration, (v) => {
        settings.disableChromaticAberration = v;
        onChange();
      }),
      toggle('Disable sensor grain', settings.disableGrain, (v) => {
        settings.disableGrain = v;
        onChange();
      }),
      toggle('High-contrast HUD', settings.highContrastHud, (v) => {
        settings.highContrastHud = v;
        onChange();
      }),
      slider('HUD scale', 0.8, 1.6, 0.05, settings.hudScale, (v) => {
        settings.hudScale = v;
        onChange();
      }),
      toggle('RWR visual only (enlarged)', settings.rwrVisualOnly, (v) => {
        settings.rwrVisualOnly = v;
        onChange();
      }),
      toggle('Subtitles for radio', settings.subtitles, (v) => {
        settings.subtitles = v;
        onChange();
      }),
      toggle('Hold inputs become toggles', settings.holdToToggle, (v) => {
        settings.holdToToggle = v;
        onChange();
      }),
      toggle('Full assist', settings.fullAssist, (v) => {
        settings.fullAssist = v;
        onChange();
      }),
    ]),
  );

  return root;
}

function group(title: string, rows: readonly HTMLElement[]): HTMLDivElement {
  const section = document.createElement('div');
  section.className = 'vz-group';
  const heading = document.createElement('h2');
  heading.textContent = title;
  section.appendChild(heading);
  for (let i = 0; i < rows.length; i += 1) section.appendChild(rows[i]);
  return section;
}

function row(labelText: string): { row: HTMLDivElement; label: HTMLLabelElement } {
  const element = document.createElement('div');
  element.className = 'vz-setting';
  const label = document.createElement('label');
  label.textContent = labelText;
  element.appendChild(label);
  return { row: element, label };
}

function slider(
  labelText: string,
  min: number,
  max: number,
  step: number,
  value: number,
  onInput: (value: number) => void,
): HTMLDivElement {
  const { row: element, label } = row(labelText);
  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  const output = document.createElement('output');
  output.textContent = value.toFixed(2);
  const id = `vz-${labelText.replace(/\W+/g, '-').toLowerCase()}`;
  input.id = id;
  label.htmlFor = id;
  input.addEventListener('input', () => {
    const next = Number(input.value);
    output.textContent = next.toFixed(2);
    onInput(next);
  });
  element.append(input, output);
  return element;
}

function toggle(labelText: string, value: boolean, onChange: (value: boolean) => void): HTMLDivElement {
  const { row: element, label } = row(labelText);
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.checked = value;
  const id = `vz-${labelText.replace(/\W+/g, '-').toLowerCase()}`;
  input.id = id;
  label.htmlFor = id;
  input.addEventListener('change', () => onChange(input.checked));
  const spacer = document.createElement('span');
  element.append(input, spacer);
  return element;
}

function choice(
  labelText: string,
  options: readonly string[],
  value: string,
  onChange: (value: string) => void,
): HTMLDivElement {
  const { row: element, label } = row(labelText);
  const select = document.createElement('select');
  for (let i = 0; i < options.length; i += 1) {
    const option = document.createElement('option');
    option.value = options[i];
    option.textContent = options[i];
    if (options[i] === value) option.selected = true;
    select.appendChild(option);
  }
  const id = `vz-${labelText.replace(/\W+/g, '-').toLowerCase()}`;
  select.id = id;
  label.htmlFor = id;
  select.addEventListener('change', () => onChange(select.value));
  const spacer = document.createElement('span');
  element.append(select, spacer);
  return element;
}
